import * as React from "react";
import {
    Alert,
    Button,
    Header,
    SpaceBetween,
    Spinner,
    Tabs
} from "@amzn/awsui-components-react";
import { PageOverviewTab } from "../../components/page/PageOverview";
import { AppContext } from "../../setup/context";
import { useAPI } from "../../hooks/api-hook";
import { UIModel } from "../../model/ui-model";
import { EntityFactory, IPage, Page, PageLibraryFactory } from "@amzn/ask-legal-domain";
import { PageDraftDetail } from "../../components/page-draft/PageDraftDetail";
import { EntityPermissionDetail } from "../../components/common/EntityPermissionComp";
import { PageVersionHistory } from "../../components/page/PageVersionHistoryTab";
import { PagePreferencesTab } from "../../components/page/PagePreferencesTab";
import { IdentityLink } from "../../components/common/IdentityLink";
import { PermissionDenied } from "../redirects/PermissionDenied";
import { PageNotFound } from "../redirects/PageNotFound";
import { useNavigate } from "react-router";
import { getQueryParameterMap } from "../../utils/common-utils";
import { useLocation, useNavigationType } from "react-router-dom";
import { PermissionsBaselineSection } from "../../components/common/PermissionsBaselineSection";
import { AfrEnabledSection } from "../../components/common/AfrEnabledSection";
import { KnowledgeBaseOptInSection } from "../../components/knowledge-base/KnowledgeBaseOptInSection";
import { Preference } from "../../setup/preference";

export const PagePortal = (props: {
    id: string;
    activeTabId?: string;
}) => {
    const context = React.useContext(AppContext);
    const [isAgenticUI] = React.useState(() => context.hasLab(Preference.Lab.AgenticUI));
    const navigate = useNavigate();
    const loadPageRunner = useAPI(
        context.getPageAPI().checkAdminAccess
    );
    const pageDataState = UIModel.State.use<Page.Data>({});
    const DEFAULT_TAB_ID = "overview";
    const [activeTabId, setActiveTabId] = React.useState(props.activeTabId ? props.activeTabId : DEFAULT_TAB_ID);
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [copyButtonText, setCopyButtonText] = React.useState<string>("Link to Live Page");
    const location = useLocation();
    const navigationType = useNavigationType();
    const baselinePagePermissionsRunner = useAPI(context.getPageAPI().updateMetadata);
    const loadPermissionRunner = useAPI(context.getEntityPermissionAPI().load);
    const [isPublic, setIsPublic] = React.useState<boolean | null>(null);

    const protectedEditorUserIds = React.useMemo(() => {
        const ids = pageDataState.value ? [
            pageDataState.value.pageOwner?.id,
            ...(pageDataState.value.secondaryPageOwners || []).map(o => o.id)
        ].filter(Boolean) as string[] : [];
        return ids.length ? ids : undefined;
    }, [pageDataState.value]);

    const protectedEditorTeamIds = React.useMemo(
        () => pageDataState.value?.secondaryPageOwnerTeams?.length
            ? pageDataState.value.secondaryPageOwnerTeams.map(t => t.teamId)
            : undefined,
        [pageDataState.value]
    );

    const setTabURL = (tabId: string): string => {
        const currenthash = window.location.hash.slice(1).split("?")[0];
        // Clear any other params currently since they have no meaning across tabs
        return `${currenthash}?tab=${tabId}`;
    };

    const baselinePagePermissions = () => {
        baselinePagePermissionsRunner.submitRun(
            IPage.UpdatePageMetadataInput.create({
                baselinePagePermissions: true,
                targetRef: EntityFactory.toEntityRef(pageDataState.value)
            })
        );
    };

    React.useEffect(() => {
        // Hook to set default entry tabId url
        const queryParamMap = getQueryParameterMap();
        if (!queryParamMap.get("tab")) {
            window.history.replaceState(
                null,
                "",
                `${window.location.hash}?tab=${DEFAULT_TAB_ID}`
            );
        }
    }, []);

    React.useEffect(() => {
        loadPageRunner.submitRun(
            props.id
        );
    }, [props.id]);

    React.useEffect(() => {
        if (navigationType === "POP") {
            const urlTab = location.search.slice(1).split("&").map(v => v.split("=")).filter(x => x[0] === "tab")[0];
            if (!urlTab) {
                setActiveTabId(DEFAULT_TAB_ID);
                return;
            }
            setActiveTabId(urlTab[1]);
        }
    }, [location, navigationType]);

    React.useEffect(() => {
        if (loadPageRunner.status === "Succeeded") {
            pageDataState.setValue(loadPageRunner.data.output);
            // Load the parent page library's permission to check if it's public
            const plId = PageLibraryFactory.fromEntityId(props.id);
            loadPermissionRunner.submitRun(plId);
        }
    }, [loadPageRunner.status]);

    React.useEffect(() => {
        if (loadPermissionRunner.status === "Succeeded") {
            setIsPublic(!!loadPermissionRunner.data.output.isPublicViewOnly);
        } else if (loadPermissionRunner.status === "Error") {
            // The parent page library's EntityPermission couldn't be loaded. A 404 means the library
            // was deleted but this page was left behind (orphaned) — handled in render by showing
            // PageNotFound. For any other/transient error, fail safe to "not public" rather than
            // leaving the flag unresolved or surfacing a raw error.
            setIsPublic(false);
        }
    }, [loadPermissionRunner.status]);

    // An orphaned page — one whose parent page library (and the library's EntityPermission) no longer
    // exists — should read as "not found", matching the live page (getPageAPI().load2 validates the
    // parent library and 404s). The admin portal loads the page directly via checkAdminAccess, so we
    // detect the orphan here from the parent-library permission 404.
    const parentLibraryMissing =
        loadPermissionRunner.status === "Error" &&
        loadPermissionRunner.data.err.code === 404;

    const getLivePageUrl = (): string => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/#/page/${props.id}/live`;
    };

    const handleCopyLivePageLink = async () => {
        const livePageUrl = getLivePageUrl();
        try {
            await navigator.clipboard.writeText(livePageUrl);
            setCopyButtonText("Link Copied!");
            setTimeout(() => {
                setCopyButtonText("Link to Live Page");
            }, 1000);
        } catch (err) {
            console.error("Failed to copy link:", err);
        }
    };

    if (parentLibraryMissing) {
        return <PageNotFound/>;
    }

    return (
        <React.Fragment>
            <SpaceBetween size="s" direction="vertical">
                {loadPageRunner.status === "Succeeded" && !!pageDataState.value && pageDataState.value.deprecated &&
                    <Alert
                        type="info"
                        header="Deprecated">
                        This page was last updated on {
                            pageDataState.value.lastModifiedDate
                        } by&nbsp;
                        <IdentityLink
                            id={pageDataState.value.lastModifiedBy.id}
                            name={pageDataState.value.lastModifiedBy.name}
                        /> with message "{pageDataState.value.lastModifiedCommit}"
                    </Alert>
                }
                {isAgenticUI && (
                    <Header
                        id="pageHeader"
                        variant="h1"
                        actions={loadPageRunner.status === "Succeeded" && (
                            <Button
                                iconName="copy"
                                onClick={handleCopyLivePageLink}
                                variant="primary"
                                ariaLabel="Click to copy and share the link with users"
                            >
                                {copyButtonText}
                            </Button>
                        )}
                    >
                        Page: {props.id.split("-")[2]}
                    </Header>
                )}
                {!isAgenticUI &&
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Header id="pageHeader" variant="h1">Page: {props.id.split("-")[2]}</Header>
                    {loadPageRunner.status === "Succeeded" && (
                        <Button
                            iconName="copy"
                            onClick={handleCopyLivePageLink}
                            variant="primary"
                            ariaLabel="Click to copy and share the link with users"
                            wrapText={false}
                        >
                            {copyButtonText}
                        </Button>
                    )}
                </div>
                }
                <React.Fragment>
                    {loadPageRunner.status === "Running" && <Spinner />}
                    {loadPageRunner.status === "Error" && (
                        loadPageRunner.data.err.code === 403 ?
                            <PermissionDenied entityId={props.id} accessType="PageAdminPortal"/> : <PageNotFound/>
                    )}
                    {loadPageRunner.status === "Succeeded" && !pageDataState.value && <Spinner />}
                    {loadPageRunner.status === "Succeeded" && !!pageDataState.value &&
                        <Tabs
                            tabs={[
                                {
                                    label: "Overview",
                                    id: "overview",
                                    content:  <PageOverviewTab
                                        state={pageDataState}
                                        reload={loadPageRunner.reload}
                                        setActiveTab={setActiveTabId}
                                    />
                                },
                                {
                                    label: "Permissions",
                                    id: "permissions",
                                    content: <SpaceBetween size="l">
                                        <PermissionsBaselineSection
                                            permissionsBaselinedDate={pageDataState.value.permissionsBaselinedDate}
                                            runner={baselinePagePermissionsRunner}
                                            onBaseline={baselinePagePermissions}
                                            onBaselineSuccess={(output) => pageDataState.setValue(output)}
                                        />
                                        <EntityPermissionDetail.PreloadComp
                                            id={props.id}
                                            protectedEditorUserIds={protectedEditorUserIds}
                                            protectedEditorTeamIds={protectedEditorTeamIds}
                                        />
                                        {isPublic === false && <AfrEnabledSection
                                            page={pageDataState.value}
                                            onUpdate={(updated) => pageDataState.setValue(updated)}
                                        />}
                                    </SpaceBetween>
                                },
                                {
                                    label: "Draft",
                                    id: "draftPage",
                                    content: <PageDraftDetail.Preload
                                        pageRef={EntityFactory.toEntityRef(pageDataState.value)}
                                        pageData={pageDataState.value}
                                        setIsEditing={setIsEditing}
                                    />
                                },
                                {
                                    label: "Version History",
                                    id: "versionHistory",
                                    content: <PageVersionHistory.TableView state={pageDataState}/>
                                },
                                {
                                    label: "Preferences",
                                    id: "preferences",
                                    content: <SpaceBetween size="l">
                                        <KnowledgeBaseOptInSection
                                            page={pageDataState.value}
                                            isRestricted={isPublic === false}
                                            onUpdate={(updated) => pageDataState.setValue(updated)}
                                        />
                                        <PagePreferencesTab
                                            pageId={props.id}
                                            containerIds={pageDataState.value.sections.flatMap(
                                                section => section.containers.map(container => container.id)
                                            )}
                                            containerMetadata={pageDataState.value.sections.flatMap(
                                                section => section.containers
                                            ).reduce((map, container) => {
                                                map[container.id] = container;
                                                return map;
                                            }, {})}
                                        />
                                    </SpaceBetween>
                                }
                            ]}
                            activeTabId={activeTabId}
                            onChange={(e) => {
                                if (isEditing) {
                                    window.alert("You have unsaved changes. Please save them to continue");
                                    return;
                                }
                                setActiveTabId(e.detail.activeTabId);
                                navigate(setTabURL(e.detail.activeTabId));
                            }}
                        />
                    }
                </React.Fragment>
            </SpaceBetween>
        </React.Fragment>
    );
};
